import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Delete, ModeEdit } from '@mui/icons-material';
import { Box, CircularProgress, Divider, SpeedDialAction } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { ConfirmModal, InfoItem, ViewPage } from '/src/components';
import { useAdjustmentStore, useAssetStore } from '/src/context';
import { toastMessages } from '/src/lib/messages';
import { Note } from '/src/lib/models';
import { CRUDMode, DataModelType } from '/src/lib/types';
import { getSpeedDialActionProps } from '/src/utils';

/**
 * View Adjustment Page
 */
export const ViewAdjustmentPage: React.FC = observer(() => {
  /** Context **/
  const adjustmentStore = useAdjustmentStore();
  const assetStore = useAssetStore();
  const params = useParams();
  const navigate = useNavigate();

  /** State **/
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [files] = useState<string[]>([]);

  /**
   * Array of booleans indicating which sidebar items are open.
   *
   * @remarks
   * Each item's index in the array corresponds to its position in the sidebar.
   */
  const [sidebarItems, setSidebarItems] = useState<boolean[]>([
    false, // Notes
    false, // Documents
  ]);

  /** Computed **/
  const adjustment = adjustmentStore.currentAdjustment;

  /** Methods **/

  /**
   * Removes the Adjustment.
   *
   * @remarks
   * Redirects to the Adjustments List afterwards.
   */
  const remove = async () => {
    if (!adjustment) return;

    try {
      await adjustmentStore.remove(adjustment._id);

      // Show alert
      toast.success(toastMessages.deleteAdjustment.success);

      // Redirect to adjustments
      navigate('/adjustments');
    } catch (err) {
      console.error(err);
      toast.success(toastMessages.deleteAdjustment.error);
    }
  };

  /**
   * Updates the Notes list.
   */
  const updateNotes = async (notes: Note[]) => {
    if (!adjustment) return;

    const response = await adjustmentStore.update({ ...adjustment, notes });
    if (!response) throw new Error(`Error updating notes for Adjustment ID ${adjustment._id}`);

    adjustmentStore.currentAdjustment = response;
  };

  /**
   * Updates the Documents list.
   *
   * @param files List of Document URLs.
   */
  const updateFiles = async (files: string[]) => {
    if (!adjustment) return;

    const response = await adjustmentStore.update({ ...adjustment, files });
    if (!response) throw new Error(`Error updating files for Adjustment ID ${adjustment._id}`);

    adjustmentStore.currentAdjustment = response;
  };

  /** Effects **/

  useEffect(() => {
    if (!params.adjustmentId) return;

    Promise.all([adjustmentStore.get(params.adjustmentId), assetStore.query()]);
  }, [params.adjustmentId]);

  /** Render **/
  return (
    <ViewPage
      modelName={adjustment?.name ?? ''}
      modelType={DataModelType.Adjustment}
      sidebarItems={sidebarItems}
      setSidebarItems={setSidebarItems}
      sidebarFlex="35"
      contentFlex="65"
      noteOptions={{
        mode: CRUDMode.View,
        notes: adjustment?.notes ?? [],
        onChange: updateNotes,
      }}
      documentOptions={{
        mode: CRUDMode.Edit,
        files: files ?? [],
        onChange: updateFiles,
      }}
      speedDialActions={[
        <SpeedDialAction
          key="edit-button"
          icon={<ModeEdit className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Edit, 'Edit Adjustment')}
          onClick={() => navigate(`/adjustments/${adjustment?._id}/edit`)}
        />,
        <SpeedDialAction
          key="delete-button"
          icon={<Delete className="material-icons" />}
          slotProps={getSpeedDialActionProps(CRUDMode.Remove, 'Delete Adjustment')}
          onClick={() => setShowConfirmDelete(true)}
        />,
      ]}
      modals={
        <ConfirmModal
          content="Are you sure you want to delete this Adjustment?"
          open={showConfirmDelete}
          setOpen={setShowConfirmDelete}
          onOk={() => remove()}
          label="Confirm Adjustment Deletion"
          okLabel="Delete"
          cancelLabel="Cancel"
        />
      }
    >
      {adjustment ? (
        <>
          <InfoItem title="Effective Date" value={adjustment?.effectiveDate} date />
          <Divider />
          <InfoItem title="Asset" value={adjustment?.asset?.name} url={`/assets/${adjustment?.asset?._id}`} />
          <Divider />
          <InfoItem title="Original Asset Value" value={adjustment?.assetValue} currency />
          <InfoItem title="Adjustment Amount" value={adjustment?.amount} currency />
          <Divider />
          <InfoItem title="Resulting Asset Value" bold value={adjustment?.resultingAssetValue} currency />
        </>
      ) : (
        <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
          <CircularProgress className="md-primary" md-mode="indeterminate" />
        </Box>
      )}
    </ViewPage>
  );
});
